import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import ScrollContainer from './common/ScrollContainer';
import StartScreen, { StartScreenContainer } from './common/StartScreen';
import { defaultProps, propTypes } from '../ModelDetail/propsFile';
import { ID_TEXTS } from '../../utils/constants';
import {
  createFadeOutColorLine,
  createImageRotationTimeline,
  createShowFeatureTimeline,
  createStartScreenTimeline,
} from './common/timeline';
import Feature, {
  FeatureStack,
  FeatureStackCentered,
  FeatureContainer,
  FeatureItem,
  FeatureBottom,
  FeatureItemBottom,
} from './common/Feature';
import Image, {
  ColorLine,
  ImageContainer,
  ImageController,
  ImageWrapper,
} from './common/Image';
import { DEFAULT_360_IMAGE_SIZE } from '../../utils/image';

const TOTAL_360_IMGS = 36;
const PRELOADING_THRESHOLD = Math.ceil(TOTAL_360_IMGS / 8);

const imageUrls = Array.from({ length: TOTAL_360_IMGS }).map(
  (_, index) =>
    `https://app.irobot.mdsrv.media/${DEFAULT_360_IMAGE_SIZE}/public-assets/product-demo-v2/intros/rj9plus/Roomba_j9plus_360Images_${
      index + 1
    }.webp`
);

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export default function TemplateJ9Plus({
  bgColor,
  logo,
  logoAlt,
  modelName,
  subtitleFont,
  subtitleColor,
  title,
  titleFont,
  titleColor,
  accentColor,
  featuresTitleColor,
  featuresModelNameColor,
  featuresDescriptionColor,
  features,
}) {
  const [containerHeight, setContainerHeight] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(1);

  const scrollContainerRef = useRef(null);
  const mainTimeLineRef = useRef(null);
  const scrollTriggerRef = useRef(null);
  const scrollToRef = useRef(null);

  function handleImageLoad(e) {
    setImageHeight(e.target.clientHeight);
    setLoadedImages(counter => counter + 1);
  }

  function handleNext(label, duration = 3) {
    const mainTimeline = mainTimeLineRef.current;
    const scrollTrigger = scrollTriggerRef.current;
    if (mainTimeline && scrollTrigger) {
      const targetLabel = mainTimeline.labels[label];
      if (targetLabel) {
        const percent = targetLabel / mainTimeline.totalDuration();
        const scrollPos =
          scrollTrigger.start +
          (scrollTrigger.end - scrollTrigger.start) * percent;
        if (scrollToRef.current) {
          scrollToRef.current.kill();
        }
        scrollToRef.current = gsap.to(window, {
          duration,
          scrollTo: scrollPos,
          onComplete: () => {
            scrollToRef.current = null;
          },
        });
      }
    }
  }

  function handleEnd() {
    if (scrollToRef.current) {
      scrollToRef.current.kill();
    }
    scrollToRef.current = gsap.to(window, {
      duration: 1,
      scrollTo: `#${ID_TEXTS.MAIN_CONTENT_REF}`,
      onComplete: () => {
        scrollToRef.current = null;
      },
    });
  }

  useEffect(() => {
    function handleWrapperChange() {
      setContainerHeight(document.documentElement.clientHeight);
    }
    handleWrapperChange();

    window.addEventListener('resize', handleWrapperChange);
    window.addEventListener('deviceorientation', handleWrapperChange);

    return () => {
      window.removeEventListener('resize', handleWrapperChange);
      window.removeEventListener('deviceorientation', handleWrapperChange);
    };
  }, []);

  useEffect(() => {
    if (loadedImages >= PRELOADING_THRESHOLD) {
      setLoading(false);
    }
  }, [loadedImages]);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!loading && element && imageHeight > 0) {
      const startScreenTimeline = createStartScreenTimeline({ element });
      const createFeatureOneTimeline = () => {
        const fadeOutColorLine = createFadeOutColorLine({ element });
        const showFeature = createShowFeatureTimeline({
          element,
          nextFeature: `#${ID_TEXTS.FEATURE_1}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 1,
          endFrame: 19,
          attributes: {
            y: -imageHeight / 2,
            x: 200,
            scale: 2,
            transformOrigin: 'center top',
          },
          onUpdate: index => setCurrentImageIndex(index),
        });
        return gsap
          .timeline()
          .add(fadeOutColorLine)
          .add(showFeature, 0)
          .add(rotateImage, 0);
      };
      const createFeatureTwoTimeline = () => {
        const showFeature = createShowFeatureTimeline({
          element,
          prevFeature: `#${ID_TEXTS.FEATURE_1}`,
          nextFeature: `#${ID_TEXTS.FEATURE_2}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 19,
          endFrame: 1,
          attributes: {
            x: 0,
            y: containerHeight / 2,
            scale: (containerHeight / imageHeight) * 1.25,
            transformOrigin: 'center top',
          },
          onUpdate: index => setCurrentImageIndex(index),
        });
        return gsap.timeline().add(rotateImage, 0).add(showFeature, 0);
      };
      const createFeatureThreeTimeline = () => {
        const showFeature = createShowFeatureTimeline({
          element,
          prevFeature: `#${ID_TEXTS.FEATURE_2}`,
          nextFeature: `#${ID_TEXTS.FEATURE_3}`,
        });
        const rotateImage = createImageRotationTimeline({
          element,
          startFrame: 1,
          endFrame: 14,
          attributes: {
            y: containerHeight / 2 - imageHeight,
            scale: 2,
            transformOrigin: 'center top',
          },
          onUpdate: index => setCurrentImageIndex(index),
        });

        return gsap.timeline().add(rotateImage, 0).add(showFeature, 0);
      };

      const mainTimeLine = gsap.timeline();
      mainTimeLine.addLabel('step1');
      mainTimeLine.add(startScreenTimeline, '+=0.5');
      mainTimeLine.addLabel('step2');
      mainTimeLine.add(createFeatureOneTimeline(), '+=0.5');
      mainTimeLine.addLabel('step3');
      mainTimeLine.add(createFeatureTwoTimeline(), '+=0.5');
      mainTimeLine.addLabel('step4');
      mainTimeLine.add(createFeatureThreeTimeline(), '+=0.5');
      mainTimeLine.set({}, {}, '+=0.5');
      mainTimeLine.addLabel('step5');

      const scrollTrigger = ScrollTrigger.create({
        animation: mainTimeLine,
        trigger: element,
        start: 'top top',
        end: '+=400%',
        scrub: true,
        pin: true,
      });

      mainTimeLineRef.current = mainTimeLine;
      scrollTriggerRef.current = scrollTrigger;
    }
  }, [containerHeight, imageHeight, loading]);

  return (
    <ScrollContainer bgColor={bgColor} ref={scrollContainerRef}>
      {loading || containerHeight === 0 ? <Loader /> : null}
      {containerHeight > 0 ? (
        <StartScreenContainer
          id={ID_TEXTS.INTRO_CONTENT}
          height={Math.ceil(containerHeight * 0.7)}
        >
          <StartScreen
            logo={logo}
            logoAlt={logoAlt}
            model={modelName}
            modelFont={subtitleFont}
            modelColor={subtitleColor}
            title={title}
            titleFont={titleFont}
            titleColor={titleColor}
            accentColor={accentColor}
            height={Math.ceil(containerHeight * 0.7)}
            onNext={() => handleNext('step3', 2)}
          />
        </StartScreenContainer>
      ) : null}
      <ImageContainer id={ID_TEXTS.IMAGES_CONTAINER} height={containerHeight}>
        <ImageController height={Math.ceil(containerHeight * 0.3 * 2)}>
          {imageUrls.map((src, index) => (
            <ImageWrapper key={`j9+-image-${index + 1}`}>
              <Image
                src={src}
                alt={`j9+ 360-image frame ${index + 1}`}
                active={index + 1 === currentImageIndex}
                onLoad={handleImageLoad}
              />
            </ImageWrapper>
          ))}
          <ColorLine
            id={ID_TEXTS.COLOR_LINE}
            color={accentColor}
            marginTop={imageHeight / 2}
          />
        </ImageController>
      </ImageContainer>
      <FeatureContainer>
        <FeatureStack id={ID_TEXTS.FEATURE_1}>
          <FeatureItem />
          <FeatureItem>
            <FeatureBottom
              icon={features[0]?.icon_url}
              iconAlt={features[0]?.icon_alt_txt}
              model={features[0]?.subtitle}
              modelColor={featuresModelNameColor}
              modelFont={subtitleFont}
              title={features[0]?.title}
              titleColor={featuresTitleColor}
              titleFont={titleFont}
              description={features[0]?.description}
              descriptionColor={featuresDescriptionColor}
              accentColor={accentColor}
              footnote={features[0]?.legal_footnote}
              onNext={() => handleNext('step4', 1.5)}
              overlay
            />
          </FeatureItem>
        </FeatureStack>
        <FeatureStack id={ID_TEXTS.FEATURE_2}>
          <FeatureItemBottom>
            <Feature
              icon={features[1]?.icon_url}
              iconAlt={features[1]?.icon_alt_txt}
              model={features[1]?.subtitle}
              modelColor={featuresModelNameColor}
              modelFont={subtitleFont}
              title={features[1]?.title}
              titleColor={featuresTitleColor}
              titleFont={titleFont}
              description={features[1]?.description}
              descriptionColor={featuresDescriptionColor}
              accentColor={accentColor}
              footnote={features[1]?.legal_footnote}
              onNext={() => handleNext('step5', 2)}
            />
          </FeatureItemBottom>
          <FeatureItem />
        </FeatureStack>
        <FeatureStackCentered id={ID_TEXTS.FEATURE_3}>
          <Feature
            icon={features[2]?.icon_url}
            iconAlt={features[2]?.icon_alt_txt}
            model={features[2]?.subtitle}
            modelColor={featuresModelNameColor}
            modelFont={subtitleFont}
            title={features[2]?.title}
            titleColor={featuresTitleColor}
            titleFont={titleFont}
            description={features[2]?.description}
            descriptionColor={featuresDescriptionColor}
            accentColor={accentColor}
            footnote={features[2]?.legal_footnote}
            overlay
            onNext={() => handleEnd()}
          />
        </FeatureStackCentered>
      </FeatureContainer>
    </ScrollContainer>
  );
}

TemplateJ9Plus.propTypes = propTypes;
TemplateJ9Plus.defaultProps = defaultProps;

const Loader = styled.div``;
